import React, { useState } from "react";
import { LinkedFileItem } from "./LinkedFileItem";

import { LINKED_ITEM_CONTAINER_HEIGHT } from "./LinkedItemContainer";
import { graphql, useFragment, useMutation } from "react-relay";
import { Box, useLayoutContext } from "arkh";

import { AttachedContent_files$key } from "./__generated__/AttachedContent_files.graphql";
import { AttachedContent_products$key } from "./__generated__/AttachedContent_products.graphql";
import { LinkedStoreProductItem } from "./LinkedStoreProductItem";
import { AttachedContentTodo_todo$key } from "./__generated__/AttachedContentTodo_todo.graphql";
import { AttachedContentTodoUnlinkProductMutation } from "./__generated__/AttachedContentTodoUnlinkProductMutation.graphql";

function ItemWrapper(props: { children: React.ReactNode; columns: number }) {
  const layout = useLayoutContext();
  return (
    <Box
      style={{
        width: `${100 / props.columns}%`,
        height: LINKED_ITEM_CONTAINER_HEIGHT + layout.space.medium,
      }}
      spaceRight="medium"
      spaceBottom="medium"
    >
      {props.children}
    </Box>
  );
}

export function AttachedContentTodo(props: {
  todo: AttachedContentTodo_todo$key;
  openFile: (fileId: string) => void;
  openProduct: (productId: string) => void;
  canEditAttachedContent: boolean;
}) {
  const todo = useFragment(
    graphql`
      fragment AttachedContentTodo_todo on Todo {
        id
        files {
          ...AttachedContent_files
        }
        linkedProducts {
          ...AttachedContent_products
        }
      }
    `,
    props.todo
  );

  const [unLinkProduct] = useMutation<AttachedContentTodoUnlinkProductMutation>(
    graphql`
      mutation AttachedContentTodoUnlinkProductMutation(
        $todoId: ID!
        $productId: ID!
      ) {
        todo(todoId: $todoId) {
          unlinkProduct(productId: $productId) {
            linkedProducts {
              id
            }
          }
        }
      }
    `
  );

  const hasLinkedContent =
    todo.files.length > 0 || todo.linkedProducts.length > 0;
  if (!hasLinkedContent) {
    return null;
  }
  return (
    <AttachedContent
      files={todo.files}
      products={todo.linkedProducts}
      openFile={({ fileId }) => {
        props.openFile(fileId);
      }}
      openProduct={props.openProduct}
      unlinkProduct={
        props.canEditAttachedContent
          ? (productId) => {
              unLinkProduct({
                variables: {
                  todoId: todo.id,
                  productId: productId,
                },
              });
            }
          : undefined
      }
    />
  );
}
export function AttachedContent(props: {
  files: AttachedContent_files$key;
  products: AttachedContent_products$key;
  openFile: (file: {
    fileId: string;
    fileName: string;
    fileType: string;
    url: string;
  }) => void;
  openProduct: (productId: string) => void;
  unlinkProduct?: (productId: string) => void;
}) {
  const products = useFragment(
    graphql`
      fragment AttachedContent_products on StoreProduct @relay(plural: true) {
        id
        name
        ...LinkedStoreProductItem_StoreProduct
      }
    `,
    props.products
  );

  const files = useFragment(
    graphql`
      fragment AttachedContent_files on File @relay(plural: true) {
        id
        name
        type
        url
        ...LinkedFileItem_file
      }
    `,
    props.files
  );

  const [columnCapacity, setColumnCapacity] = useState(2);
  const numberOfItems = products.length + files.length;
  const numColumns =
    products.length + files.length < 2
      ? 1
      : Math.min(columnCapacity, numberOfItems);

  const layout = useLayoutContext();
  if (products.length === 0 && files.length === 0) {
    return null;
  }
  return (
    <Box spaceHorizontal="medium" spaceBottom="medium">
      <Box
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          flex: 1,
          marginRight: -layout.space.medium,
          marginBottom: -layout.space.medium,
        }}
        onLayout={(event) => {
          setColumnCapacity(
            Math.max(1, Math.floor(event.nativeEvent.layout.width / 200))
          );
        }}
      >
        {products.map((product) => (
          <ItemWrapper key={product.id} columns={numColumns}>
            <LinkedStoreProductItem
              product={product}
              key={product.id}
              onPressProduct={() => {
                props.openProduct(product.id);
              }}
              unLinkProduct={
                props.unlinkProduct
                  ? () => {
                      props.unlinkProduct?.(product.id);
                    }
                  : undefined
              }
            />
          </ItemWrapper>
        ))}
        {files.map((file) => (
          <ItemWrapper key={file.id} columns={numColumns}>
            <LinkedFileItem
              file={file}
              key={file.id}
              onPress={() => {
                props.openFile({
                  fileId: file.id,
                  fileName: file.name ?? "",
                  fileType: file.type ?? "",
                  url: file.url,
                });
              }}
            />
          </ItemWrapper>
        ))}
      </Box>
    </Box>
  );
}
